import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  alpha,
} from "@mui/material";
import { PlayCircleOutline } from "@mui/icons-material";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode } from "../utils";
import BrandedPageFooter from "../components/BrandedPageFooter";
import { useNavigate } from "react-router-dom";

interface VideosProps {
  mode: string;
}

interface VideoItem {
  id: string;
  title: string;
  description: string;
  // Google Drive file ID extracted from share link
  driveFileId: string;
}

const Videos: React.FC<VideosProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { whitelabelSettings, whitelabelLogos } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  // Get logo URL with dark mode support
  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";

  // Video content - Google Drive file IDs
  const videos: VideoItem[] = [
    {
      id: "1",
      title: "Company Management",
      description: "Learn how to manage your company and configure your account.",
      driveFileId: "1Rxx7uXJzjFVQyfwf5pOmzPkDsnDjNUgZ",
    },
    {
      id: "2",
      title: "Company Getting Started",
      description: "Get started with your company and explore the platform features.",
      driveFileId: "14VmjPuhRzwYkWZ7KpvhEns1_7sdCv18C",
    },
    {
      id: "3",
      title: "Inboxes",
      description: "Learn how to set up and manage your inboxes.",
      driveFileId: "1Gv3hzdXp7c0O-vxcPKhuacmo8pLZ8Nc0",
    },
    {
      id: "4",
      title: "Content Engine",
      description: "Learn how to use the content engine to create and manage content.",
      driveFileId: "1rJJAfhn7hSqA50nv587fpdrvhmbWsp-i",
    },
    {
      id: "5",
      title: "Sequences",
      description: "Learn how to create and manage automated sequences.",
      driveFileId: "12hSkGbAoXOaW8JsEw_HrQXhcEwFqjKiw",
    },
    {
      id: "6",
      title: "Smart Lists",
      description: "Learn how to create and use smart lists for lead management.",
      driveFileId: "1tNMMlv4tgPoq0Yau5CV5ZrR6ut4ar-h0",
    },
    {
      id: "7",
      title: "Agents",
      description: "Learn how to create and configure AI agents.",
      driveFileId: "10t5d5qN27f8d-IM8tXez2ODqOvsyGziX",
    },
    {
      id: "8",
      title: "Executive Assistant",
      description: "Learn how to use the Executive Assistant feature.",
      driveFileId: "1zPfLBEeb2WBn58mrcWG1E0i4amztr_DY",
    },
  ];

  // Convert Google Drive file ID to embeddable URL
  const getDriveEmbedUrl = (fileId: string) => {
    return `https://drive.google.com/file/d/${fileId}/preview`;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={getLogoUrl()}
            alt={appName}
            sx={{
              height: 48,
              mb: 3,
              objectFit: "contain",
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: instanceConfig.primaryColor }}
          >
            Video Training
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            Learn how to get the most out of {appName} with our video training.
          </Typography>
        </Container>
      </Box>

      {/* Video Content */}
      <Container maxWidth="lg" sx={{ py: 6, flex: 1 }}>
        <Grid container spacing={4}>
          {videos.map((video) => (
            <Grid item xs={12} key={video.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: alpha(instanceConfig.primaryColor, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: instanceConfig.primaryColor,
                        mr: 2,
                      }}
                    >
                      <PlayCircleOutline />
                    </Box>
                    <Box>
                      <Typography variant="h6">#{video.id} {video.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {video.description}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Video Embed */}
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      paddingTop: "56.25%", // 16:9 aspect ratio
                      borderRadius: 1,
                      overflow: "hidden",
                      bgcolor: "action.hover",
                    }}
                  >
                    <iframe
                      src={getDriveEmbedUrl(video.driveFileId)}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      title={video.title}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Coming Soon Section */}
        {videos.length === 1 && (
          <Box
            sx={{
              mt: 4,
              p: 4,
              textAlign: "center",
              bgcolor: alpha(instanceConfig.primaryColor, 0.05),
              borderRadius: 2,
              border: 1,
              borderColor: alpha(instanceConfig.primaryColor, 0.2),
            }}
          >
            <Typography variant="h6" gutterBottom>
              More Videos Coming Soon
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We're working on more training videos to help you master {appName}. Check back soon for new content!
            </Typography>
          </Box>
        )}
      </Container>

      <BrandedPageFooter currentPage="training" />
    </Box>
  );
};

export default Videos;
